import type { BuildingGraphFilterBuilding } from "building-types/buildingGraphFilter/buildingGraphFilterBuilding";
import type { FaceEdgeRow } from "building-types/buildingGraphFilter/entitySource";
import type { PropertyPath } from "building-types/buildingGraphFilter/propertyCatalog";
import { assertUnreachable } from "common/utils/assertUnreachable";
import { faceNodeGetPlaneHash } from "../entityFilterSelectors";
import {
  type EntityFilterSource,
  type PropertyDescriptorForPath,
  type PropertyValue,
} from "../entityFilterSourceTypes";

const FACE_EDGE_DESCRIPTORS: { [P in PropertyPath<"FACE_EDGE">]: PropertyDescriptorForPath<"FACE_EDGE", P> } = {
  faceEdgeIndex: { kind: "SCALAR", type: "number" },
  faceEdgeType: { kind: "SCALAR", type: "enum", values: ["OVERLAP", "EDGE"] },
  faceEdgeDot: { kind: "SCALAR", type: "number" },
  faceANodeId: { kind: "SCALAR", type: "id" },
  faceBNodeId: { kind: "SCALAR", type: "id" },
  planeHashes: { kind: "SET", type: "string" },
};

function readFaceEdgeProperty(
  row: FaceEdgeRow,
  path: PropertyPath<"FACE_EDGE">,
  building: BuildingGraphFilterBuilding,
): PropertyValue {
  switch (path) {
    case "faceEdgeIndex":
      return { kind: "SCALAR", value: row.faceEdgeIndex };
    case "faceEdgeType":
      return { kind: "SCALAR", value: row.faceEdge.faceEdgeType };
    case "faceEdgeDot":
      return { kind: "SCALAR", value: row.faceEdge.faceEdgeDot };
    case "faceANodeId":
      return { kind: "SCALAR", value: row.faceEdge.faceANodeId };
    case "faceBNodeId":
      return { kind: "SCALAR", value: row.faceEdge.faceBNodeId };
    case "planeHashes": {
      const faceNodeA = building.buildingGraph.faceNodes[row.faceEdge.faceANodeId];
      const faceNodeB = building.buildingGraph.faceNodes[row.faceEdge.faceBNodeId];
      const planeHashes = new Set<string>();
      if (faceNodeA) {
        const planeHash = faceNodeGetPlaneHash(faceNodeA, building);
        if (planeHash) {
          planeHashes.add(planeHash);
        }
      }
      if (faceNodeB) {
        const planeHash = faceNodeGetPlaneHash(faceNodeB, building);
        if (planeHash) {
          planeHashes.add(planeHash);
        }
      }
      return { kind: "SET", values: planeHashes };
    }
    default:
      return assertUnreachable(path);
  }
}

export const faceEdgeSource: EntityFilterSource<"FACE_EDGE"> = {
  sourceId: "FACE_EDGE",
  *iterateRows(building) {
    for (let faceEdgeIndex = 0; faceEdgeIndex < building.buildingGraph.faceEdges.length; faceEdgeIndex++) {
      const faceEdge = building.buildingGraph.faceEdges[faceEdgeIndex];
      if (faceEdge) {
        yield { faceEdgeIndex, faceEdge };
      }
    }
  },
  identity(row) {
    return String(row.faceEdgeIndex);
  },
  descriptor(path) {
    return FACE_EDGE_DESCRIPTORS[path];
  },
  readProperty(row, path, building) {
    return readFaceEdgeProperty(row, path, building);
  },
};
